import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, authHeaders, getToken } from "@/lib/api";
import { MessageSquare, Store, Loader2, Ticket, ArrowLeft, Trash2, CheckCircle2, Clock, ChevronRight } from "lucide-react";
import { relativeDate } from "@/lib/format";
import { toast } from "sonner";

const COUPON_STATUS = {
  used: { label: "Utilizzato", cls: "bg-slate-200 text-slate-600", icon: CheckCircle2 },
  expired: { label: "Scaduto", cls: "bg-amber-100 text-amber-700", icon: Clock },
};

export default function ClientMessages() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const load = useCallback(async () => {
    if (!getToken("client")) { navigate("/"); return; }
    setLoading(true);
    try {
      const r = await api.get("/client/messages", { headers: authHeaders("client") });
      setConversations(r.data.conversations || []);
    } catch (_) {}
    finally { setLoading(false); }
  }, [navigate]);
  useEffect(() => { load(); }, [load]);

  const active = conversations.find((c) => c.vendor_id === activeId) || null;

  const openConversation = async (c) => {
    setActiveId(c.vendor_id);
    if (c.unread > 0) {
      try {
        await api.post(`/client/conversations/${c.vendor_id}/read`, {}, { headers: authHeaders("client") });
        setConversations((cs) => cs.map((x) => x.vendor_id === c.vendor_id
          ? { ...x, unread: 0, messages: x.messages.map((m) => ({ ...m, read: true })) } : x));
        window.dispatchEvent(new Event("kn-messages-read"));
      } catch (_) {}
    }
  };

  const deleteConversation = async (vid, e) => {
    e?.stopPropagation();
    if (!window.confirm("Eliminare tutta la conversazione? I messaggi verranno rimossi dalla tua casella.")) return;
    try {
      await api.post(`/client/conversations/${vid}/delete`, {}, { headers: authHeaders("client") });
      setConversations((cs) => cs.filter((c) => c.vendor_id !== vid));
      if (activeId === vid) setActiveId(null);
      window.dispatchEvent(new Event("kn-messages-read"));
      toast.success("Conversazione eliminata");
    } catch (_) { toast.error("Errore"); }
  };

  const deleteMessage = async (mid) => {
    if (!window.confirm("Eliminare questo messaggio?")) return;
    try {
      await api.post(`/client/messages/${mid}/delete`, {}, { headers: authHeaders("client") });
      const conv = conversations.find((c) => c.vendor_id === activeId);
      const remaining = (conv?.messages || []).filter((m) => m.id !== mid);
      if (remaining.length === 0) {
        setConversations((cs) => cs.filter((c) => c.vendor_id !== activeId));
        setActiveId(null);
      } else {
        setConversations((cs) => cs.map((c) => c.vendor_id === activeId ? { ...c, messages: remaining } : c));
      }
      window.dispatchEvent(new Event("kn-messages-read"));
    } catch (_) { toast.error("Errore"); }
  };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>;

  // ---------- Conversation thread ----------
  if (active) {
    return (
      <div className="space-y-3 kn-fade-up" data-testid="client-conversation">
        <div className="flex items-center gap-3 sticky top-0 bg-[#F8F9FB]/90 backdrop-blur py-2 z-10">
          <button onClick={() => setActiveId(null)} data-testid="conv-back" className="h-10 w-10 rounded-full hover:bg-slate-100 flex items-center justify-center shrink-0"><ArrowLeft className="h-5 w-5 text-slate-600" /></button>
          <button onClick={() => navigate(`/store/${active.vendor_id}`)} className="h-10 w-10 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
            {active.logo ? <img src={active.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-5 w-5 text-slate-400" />}
          </button>
          <div className="min-w-0 flex-1">
            <p className="font-extrabold text-slate-900 truncate">{active.store_name}</p>
            <button onClick={() => navigate(`/store/${active.vendor_id}`)} className="text-[11px] text-[#E63946] font-semibold flex items-center gap-0.5">Vedi negozio <ChevronRight className="h-3 w-3" /></button>
          </div>
          <button onClick={(e) => deleteConversation(active.vendor_id, e)} data-testid={`delete-conversation-${active.vendor_id}`} className="h-10 w-10 rounded-full hover:bg-red-50 flex items-center justify-center shrink-0 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
        </div>

        <div className="space-y-3 pb-4">
          {active.messages.map((m) => {
            const meta = m.coupon_claim_id ? COUPON_STATUS[m.coupon_status] : null;
            return (
              <div key={m.id} className="flex justify-start" data-testid={`message-${m.id}`}>
                <div className="group max-w-[85%] bg-white rounded-2xl rounded-tl-md shadow-sm border border-slate-100 p-3">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{m.body}</p>
                  {m.coupon_claim_id && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button onClick={() => navigate(`/coupon/${m.coupon_claim_id}`)} data-testid={`open-coupon-${m.coupon_claim_id}`}
                        className={`inline-flex items-center gap-2 h-9 px-3 rounded-xl font-bold text-sm ${meta ? "bg-slate-100 text-slate-500" : "kn-gradient text-white"}`}>
                        <Ticket className="h-4 w-4" /> {meta ? "Vedi coupon" : "Apri coupon"} {m.coupon_discount_label ? `(${m.coupon_discount_label})` : ""}
                      </button>
                      {meta && (
                        <span data-testid={`coupon-status-${m.coupon_claim_id}`} className={`inline-flex items-center gap-1 px-2 h-6 rounded-full text-[11px] font-bold ${meta.cls}`}>
                          <meta.icon className="h-3 w-3" /> {meta.label}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3 mt-1.5">
                    <p className="text-[11px] text-slate-400">{relativeDate(m.created_at)}</p>
                    <button onClick={() => deleteMessage(m.id)} data-testid={`delete-message-${m.id}`}
                      className="text-slate-300 hover:text-red-500 opacity-60 group-hover:opacity-100 transition"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ---------- Conversation list ----------
  return (
    <div className="space-y-4 kn-fade-up" data-testid="client-messages-page">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Messaggi</h1>
        <p className="text-slate-500 text-sm">Le tue conversazioni con i negozi che segui.</p>
      </div>

      {conversations.length === 0 ? (
        <div className="kn-card p-10 text-center" data-testid="client-messages-empty">
          <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3"><MessageSquare className="h-7 w-7 text-slate-400" /></div>
          <p className="font-bold text-slate-700">Nessun messaggio</p>
          <p className="text-sm text-slate-500 mt-1">Segui i tuoi negozi preferiti per ricevere le loro offerte e comunicazioni qui.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {conversations.map((c) => (
            <div key={c.vendor_id} onClick={() => openConversation(c)} data-testid={`conversation-${c.vendor_id}`}
              className={`kn-card p-3.5 flex items-center gap-3 cursor-pointer hover:ring-1 hover:ring-[#E63946]/20 transition ${c.unread > 0 ? "bg-[#FFF7F8]" : ""}`}>
              <div className="h-12 w-12 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0 relative">
                {c.logo ? <img src={c.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-6 w-6 text-slate-400" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-bold text-slate-800 truncate flex-1">{c.store_name}</p>
                  <span className="text-[11px] text-slate-400 shrink-0">{relativeDate(c.last_at)}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className={`text-sm truncate flex-1 ${c.unread > 0 ? "text-slate-800 font-semibold" : "text-slate-500"}`}>{c.last_body}</p>
                  {c.unread > 0 && <span className="h-5 min-w-5 px-1.5 rounded-full bg-[#E63946] text-white text-[11px] font-bold flex items-center justify-center shrink-0" data-testid={`conv-unread-${c.vendor_id}`}>{c.unread}</span>}
                </div>
              </div>
              <button onClick={(e) => deleteConversation(c.vendor_id, e)} data-testid={`delete-conversation-${c.vendor_id}`}
                className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-300 hover:bg-red-50 hover:text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
